import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  buildHsbcPackage, downloadHsbcPackageFile, getHsbcPackage, getPaymentRequest,
} from "redux/erp/paymentRequestSlice";
import { ActionButton } from "./journalUi";
import HsbcFileViewer from "./HsbcFileViewer";

const STATUS = {
  ready: { label: "Ready · reviewed file", tone: "ok" },
  stale: { label: "Changed — rebuild", tone: "error" },
  failed: { label: "Could not be prepared", tone: "error" },
  missing: { label: "Not prepared", tone: "warn" },
  waiting_journal: { label: "Waiting for Payment journal (APP)", tone: "muted" },
};
const TONE = {
  ok: "tw-bg-[#ecfdf3] tw-text-[#067647] tw-border-[#abefc6]",
  warn: "tw-bg-[#fffaeb] tw-text-[#b54708] tw-border-[#fedf89]",
  error: "tw-bg-[#fef3f2] tw-text-[#b42318] tw-border-[#fecdca]",
  info: "tw-bg-[#eff8ff] tw-text-[#175cd3] tw-border-[#b2ddff]",
  muted: "tw-bg-geist-bg-2 tw-text-geist-fg-3 tw-border-geist-border",
};
const CHECK_ICON = {
  ok: "bi-check-circle-fill tw-text-[#12b76a]",
  warn: "bi-exclamation-triangle-fill tw-text-[#f79009]",
  error: "bi-x-circle-fill tw-text-[#f04438]",
  info: "bi-info-circle-fill tw-text-[#2e90fa]",
};
const RELEASE = {
  queued: "Queued for HSBC after the BOD approval",
  dispatching: "Being sent to HSBC",
  sent: "Sent to HSBC",
  rejected: "Rejected by HSBC",
  unknown: "Outcome unknown — check HSBC before any retry",
  blocked: "Release blocked",
};

const CheckList = ({ items }) => (
  <ul className="tw-m-0 tw-p-0 tw-list-none tw-divide-y tw-divide-geist-border tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border">
    {items.map((c) => (
      <li key={c.key} data-status={c.status} className="tw-grid tw-grid-cols-[16px_minmax(120px,170px)_1fr] tw-items-start tw-gap-2.5 tw-px-3 tw-py-1.5 tw-text-[13px]">
        <i className={`bi ${CHECK_ICON[c.status] || CHECK_ICON.info} tw-mt-0.5`} aria-label={c.status} />
        <span className="tw-font-medium tw-text-geist-fg">{c.label}</span>
        <span className="tw-min-w-0 tw-break-words tw-text-geist-fg-2">{c.detail}</span>
      </li>
    ))}
  </ul>
);

CheckList.propTypes = { items: PropTypes.array.isRequired };

const shortHash = (h) => (h ? `${h.slice(0, 12)}…` : "—");
const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "");

/**
 * HSBC online payment — the actual file (XML v3 pain.001 + GPG) Accounting reviews
 * before Head of Finance and BOD sign. Paid right after the BOD approves, so every
 * check is spelled out here: what is OK, what to look at, what blocks it.
 */
const HsbcPaymentFilePanel = ({ paymentRequest: pr, refreshKey }) => {
  const dispatch = useDispatch();
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await dispatch(getHsbcPackage(pr.id)).unwrap();
      setPkg(res?.data || null);
    } catch {
      /* toasted by the slice */
    }
  }, [dispatch, pr.id]);

  // Send to HSBC again changes only automatic_release, not the package status: without it
  // the panel kept an old "Outcome unknown · HTTP 503" next to a payment HSBC had accepted.
  const release = pr.automatic_release || {};
  const releaseKey = [release.state, release.reference, release.error, release.finished_at].join("|");
  useEffect(() => { load(); }, [load, refreshKey, pr.hsbc_package?.status, releaseKey]);

  const build = async () => {
    setBusy("build");
    try {
      const res = await dispatch(buildHsbcPackage(pr.id)).unwrap();
      setPkg(res?.data || null);
      toast.success(res?.message || "HSBC payment file prepared.");
      dispatch(getPaymentRequest(pr.id));
    } catch {
      // The error is toasted by the slice; reload shows the recorded failure.
      load();
    } finally {
      setBusy(null);
    }
  };

  const download = async (kind) => {
    setBusy(`file:${kind}`);
    try {
      await downloadHsbcPackageFile(pr.id, kind);
    } catch {
      toast.error("Could not download the file.");
    } finally {
      setBusy(null);
    }
  };

  if (!pkg || pkg.status === "not_required") return null;

  const status = STATUS[pkg.status] || STATUS.missing;
  // Settled by a person ("HSBC did not receive it"): the release still says "unknown" until sent again.
  const notReceived = !!pr.hsbc_package?.release_not_received;
  const traceId = !notReceived && pr.hsbc_package?.hsbc_trace_id;
  const checks = pkg.checks || [];
  const toFix = checks.filter((c) => c.status === "error").length;
  const toCheck = checks.filter((c) => c.status === "warn").length;
  const built = !!pkg.digest;
  const issues = checks.filter((c) => c.status === "error" || c.status === "warn")
    .sort((a, b) => (a.status === b.status ? 0 : a.status === "error" ? -1 : 1));
  const passed = checks.filter((c) => c.status !== "error" && c.status !== "warn");

  return (
    <section aria-label="HSBC payment file" className="payment-journal-setup tw-flex tw-flex-col tw-gap-3">
      <header className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <p className="payment-journal-section-title tw-m-0">HSBC payment file · XML v3 (pain.001) + GPG</p>
          <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-geist-fg-3">
            Built and encrypted by the HSBC gateway. Head of Finance and BOD sign exactly this content — the payment is
            sent to HSBC right after the BOD approves.
          </p>
        </div>
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-px-2.5 tw-py-0.5 tw-text-[12px] tw-font-semibold ${TONE[status.tone]}`}
          data-testid="hsbc-file-status">
          {status.label}
        </span>
      </header>

      {pkg.status === "ready" && (
        <p className="tw-m-0 tw-text-[13px] tw-text-geist-fg-2">
          <i className="bi bi-shield-check tw-mr-1 tw-text-[#12b76a]" aria-hidden="true" />
          Prepared {when(pkg.built_at)} by {pkg.built_by}{pkg.builds > 1 ? ` · build #${pkg.builds}` : ""} · journal {pkg.journal_number}
          {" "}· signed content <code title={pkg.digest}>{shortHash(pkg.digest)}</code>
        </p>
      )}
      {pkg.status === "stale" && (
        <p role="alert" className={`tw-m-0 tw-rounded-geist-sm tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-[13px] ${TONE.error}`}>
          Payment details or the Payment journal changed after the file was prepared ({when(pkg.built_at)}). Rebuild and
          review it — approvals already given must be signed again.
        </p>
      )}
      {pkg.status === "failed" && (
        <p role="alert" className={`tw-m-0 tw-rounded-geist-sm tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-[13px] ${TONE.error}`}>
          Last attempt {when(pkg.failed_at)} failed: {pkg.error}
        </p>
      )}
      {pkg.release?.state && (
        <p className={`tw-m-0 tw-rounded-geist-sm tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-[13px] ${TONE.info}`}>
          <i className="bi bi-send tw-mr-1" aria-hidden="true" />
          {notReceived ? "HSBC confirmed it did not receive the payment — it can be sent again"
            : RELEASE[pkg.release.state] || pkg.release.state}
          {pkg.release.reference ? ` · HSBC ref ${pkg.release.reference}` : ""}
          {traceId ? ` · HSBC trace id ${traceId}` : ""}
          {pkg.release.error && !notReceived ? ` · ${pkg.release.error}` : ""}
        </p>
      )}

      <div role="toolbar" aria-label="HSBC file actions" className="tw-flex tw-flex-wrap tw-gap-2">
        {pkg.allow_build && (
          <ActionButton tone={pkg.status === "ready" ? "default" : "primary"} icon="bi-arrow-repeat" loading={busy === "build"} disabled={!!busy || pkg.status === "waiting_journal"}
            onClick={build}>
            {busy === "build" ? "Preparing…" : built ? "Rebuild HSBC file" : "Prepare HSBC file"}
          </ActionButton>
        )}
        <ActionButton icon="bi-file-code" disabled={!!busy} onClick={() => setViewing(built ? "pain" : "instruction")}>
          {built ? "View XML v3" : "Preview instruction XML"}
        </ActionButton>
        {built && (
          <>
            <ActionButton icon="bi-download" loading={busy === "file:pain"} disabled={!!busy} onClick={() => download("pain")}>XML v3 (pain.001)</ActionButton>
            <ActionButton icon="bi-download" loading={busy === "file:gpg"} disabled={!!busy} onClick={() => download("gpg")}>Encrypted .gpg</ActionButton>
          </>
        )}
        <ActionButton icon="bi-arrow-clockwise" disabled={!!busy} onClick={load}>Re-check</ActionButton>
      </div>

      <div aria-label="Accounting review checklist">
        <p className="tw-m-0 tw-mb-1.5 tw-text-[12px] tw-font-semibold tw-text-geist-fg-2">
          Accounting review
          {toFix > 0 && <span className="tw-ml-2 tw-text-[#b42318]">{toFix} to fix</span>}
          {toCheck > 0 && <span className="tw-ml-2 tw-text-[#b54708]">{toCheck} to check</span>}
          {toFix === 0 && toCheck === 0 && checks.length > 0 && <span className="tw-ml-2 tw-text-[#067647]">all checked</span>}
        </p>
        {/* What needs attention first; what passed stays folded. */}
        {issues.length > 0 && <CheckList items={issues} />}
        {passed.length > 0 && (
          <details className="tw-mt-1.5 tw-text-[13px]" open={issues.length === 0}>
            <summary className="tw-cursor-pointer tw-text-[12px] tw-text-[#067647]">
              <i className="bi bi-check-circle-fill tw-mr-1" aria-hidden="true" />{passed.length} checks passed
            </summary>
            <div className="tw-mt-1.5"><CheckList items={passed} /></div>
          </details>
        )}
      </div>

      {(pkg.mapping || []).length > 0 && (
        <details className="tw-text-[13px]">
          <summary className="tw-cursor-pointer tw-font-semibold tw-text-geist-fg-2">
            What HSBC receives {built ? "(from the prepared XML v3)" : "(preview — file not prepared yet)"}
          </summary>
          <table className="tw-mt-2 tw-w-full tw-border-collapse">
            <tbody>
              {pkg.mapping.map((m) => (
                <tr key={m.label} className="tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                  <td className="tw-py-1.5 tw-pr-3 tw-text-geist-fg-3">{m.label}</td>
                  <td className="tw-py-1.5 tw-font-medium tw-text-geist-fg tw-break-all">{m.value || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {built && (
        <details className="tw-text-[12px] tw-text-geist-fg-3">
          <summary className="tw-cursor-pointer">Hashes &amp; keys</summary>
          <dl className="tw-mt-2 tw-grid tw-grid-cols-[160px_1fr] tw-gap-x-3 tw-gap-y-1 tw-m-0">
            <dt>Signed content (SHA-256)</dt><dd className="tw-m-0 tw-break-all"><code>{pkg.digest}</code></dd>
            <dt>XML v3 (SHA-256)</dt><dd className="tw-m-0 tw-break-all"><code>{pkg.pain_sha256}</code></dd>
            <dt>GPG (SHA-256)</dt><dd className="tw-m-0 tw-break-all"><code>{pkg.gpg_sha256}</code></dd>
            {pkg.recipient_fingerprint && <><dt>HSBC key</dt><dd className="tw-m-0 tw-break-all"><code>{pkg.recipient_fingerprint}</code></dd></>}
            {pkg.signer_fingerprint && <><dt>PEB signing key</dt><dd className="tw-m-0 tw-break-all"><code>{pkg.signer_fingerprint}</code></dd></>}
          </dl>
        </details>
      )}
      <HsbcFileViewer show={!!viewing} onHide={() => setViewing(null)} requestId={pr.id} built={built} initialKind={viewing || "pain"} />
    </section>
  );
};

HsbcPaymentFilePanel.propTypes = {
  paymentRequest: PropTypes.object.isRequired,
  refreshKey: PropTypes.number,
};

export default HsbcPaymentFilePanel;
